// ============================================================================
// PRÉPARER UNE PHOTO AVANT DE L'ENVOYER — redimensionner, recompresser, dans le navigateur.
//
// POURQUOI CET OUTIL EXISTE (décision utilisateur 2026-08-25). Les photos se déposent depuis un
// TÉLÉPHONE, dans la boutique ou la réserve : un iPhone récent sort du 4032×3024 pour 2 à 5 Mo, un
// Galaxy peut monter à 50 Mpx. Sans préparation, les plafonds (1 Mo, 2000 px) refuseraient presque
// toutes les photos réelles — et le gérant, téléphone en main, n'a aucun moyen de redimensionner.
// La limite deviendrait un mur, et le catalogue resterait sans visuels.
//
// ⚠️ LA PRÉPARATION NE REMPLACE PAS LA GARDE SERVEUR, elle la rend atteignable. Le navigateur n'est
// pas une autorité : `prepareUpload` continue de mesurer et de refuser. Les deux plafonds sont
// d'ailleurs DIFFÉRENTS À DESSEIN — cible 1600 px ici, mur 2000 px là-bas : si les deux valaient le
// même chiffre, un simple aléa de compression ferait échouer un dépôt parfaitement légitime.
//
// ⚠️ SORTIE EN WebP, et c'est un choix : il conserve la TRANSPARENCE (un packshot PNG détouré reste
// détouré, ce qu'un JPEG détruirait) pour un poids très inférieur. À 1600 px et qualité 0,82, une
// photo d'article pèse typiquement 150 à 300 Ko.
// ============================================================================

/** La cible de préparation — le grand côté, et la qualité de recompression. */
import { t } from '../i18n';
export const CIBLE_PX = 1600;
export const QUALITE_WEBP = 0.82;

/**
 * Côté maximal de la VIGNETTE.
 * ⚠️ 320 ET NON 34 : la liste l'affiche en 34 px, mais un écran à trois fois la densité en réclame
 * 102, et la même vignette sert la galerie de la fiche, plus grande. Viser la taille d'affichage
 * produirait exactement la pixellisation qu'on veut éviter. À 320 px WebP, une vignette pèse quelques
 * kilo-octets — vingt-cinq d'entre elles restent sous ce que coûtait UNE image pleine.
 */
export const VIGNETTE_PX = 320;
/** Une vignette n'est jamais regardée de près : la qualité y coûte du poids sans rien rendre. */
export const QUALITE_VIGNETTE = 0.7;

/** Ce qu'une préparation a produit — l'écran s'en sert pour DIRE ce qui a été fait. */
export interface PhotoPreparee {
  fichier: File;
  /** Poids d'origine, en octets — pour annoncer « 4,2 Mo → 210 Ko ». */
  octetsAvant: number;
  octetsApres: number;
  /** `false` = l'image passait déjà : on n'a rien retouché (voir `decisionDeRedimension`). */
  retouchee: boolean;
  /**
   * LA MÊME IMAGE EN PETIT — la vignette des listes, produite dans la foulée.
   *
   * ⚠️ ELLE SE FABRIQUE ICI PARCE QUE C'EST LE SEUL ENDROIT OÙ L'IMAGE EST DÉJÀ DÉCODÉE. Le serveur
   * n'embarque aucune bibliothèque d'image — et lui en ajouter une (liaisons natives, mémoire, un
   * décodeur exposé à des fichiers venus du dehors) pour redimensionner ce que le navigateur vient de
   * décoder serait payer très cher une chose déjà faite.
   * ⚠️ `null` EST UN CAS NORMAL, pas une panne : un encodeur absent ou un lot déposé avant cette
   * version n'en produit pas, et la lecture retombe sur l'image pleine. Une vignette manquante coûte
   * du poids, jamais une image cassée.
   */
  vignette: File | null;
}

/**
 * FAUT-IL RETOUCHER, ET VERS QUELLES DIMENSIONS — la décision, séparée de son exécution.
 *
 * ⚠️ FONCTION PURE, au niveau du module : le calcul de ratio est exactement le genre de règle qui se
 * trompe d'un pixel sans que personne ne le voie, et le canvas d'un navigateur n'est pas testable.
 * ⚠️ ON NE GRANDIT JAMAIS UNE IMAGE : agrandir n'ajoute aucun détail, ça ne fait que peser plus lourd
 * en donnant l'illusion d'une meilleure définition.
 * ⚠️ UNE IMAGE DÉJÀ PETITE MAIS TROP LOURDE EST QUAND MÊME RECOMPRESSÉE (`recompresser`) : un PNG de
 * 800 px peut peser 3 Mo, et le refuser sans rien tenter serait un mur de plus.
 */
export function decisionDeRedimension(
  largeur: number,
  hauteur: number,
  octets: number,
  plafondOctets: number,
  ciblePx: number = CIBLE_PX,
): { redimensionner: boolean; recompresser: boolean; largeur: number; hauteur: number } {
  const grandCote = Math.max(largeur, hauteur);
  const tropGrande = grandCote > ciblePx;
  const facteur = tropGrande ? ciblePx / grandCote : 1;
  return {
    redimensionner: tropGrande,
    recompresser: !tropGrande && octets > plafondOctets,
    // `max(1, …)` : une image de 1 px de haut ne doit pas devenir une image de 0 px, qui n'existe pas.
    largeur: Math.max(1, Math.round(largeur * facteur)),
    hauteur: Math.max(1, Math.round(hauteur * facteur)),
  };
}

/** Poids lisible — « 4,2 Mo », « 210 Ko ». Sert au compte rendu de préparation, pas à une décision. */
export function poidsLisible(octets: number): string {
  // ⚠️ UNE TAILLE DE FICHIER N'EST PAS UNE PHRASE COMPTÉE : « 1,5 Mo » ne s'accorde pas, et le
  //    nombre est DÉJÀ formaté ici. Le symbole d'unité, lui, change de langue — d'où la clé.
  if (octets >= 1024 * 1024) return t('{taille} Mo', { taille: (octets / (1024 * 1024)).toFixed(1).replace('.', ',') });
  return t('{taille} Ko', { taille: Math.round(octets / 1024) });
}

/**
 * Le motif qui empêche de préparer un fichier — `null` quand il est traitable.
 *
 * ⚠️ LE HEIC EST LE CAS QUI COMPTE. Les iPhone photographient en HEIC, mais un `<input type="file">`
 * sur iOS livre du JPEG : en pratique, le dépôt depuis un téléphone passe. Un vrai `.heic` (glissé
 * depuis un Mac) n'est décodable par AUCUN navigateur — `createImageBitmap` échouerait sur un message
 * technique. On le nomme AVANT, avec le geste qui le répare.
 */
export function motifNonPreparable(f: File): string | null {
  const nom = f.name.toLowerCase();
  if (nom.endsWith('.heic') || nom.endsWith('.heif') || f.type === 'image/heic' || f.type === 'image/heif') {
    return t('« {name} » est au format HEIC, qu’aucun navigateur ne sait ouvrir. Exportez-la en JPEG (sur iPhone : Réglages → Appareil photo → Formats → « Le plus compatible »), ou envoyez-la depuis le téléphone.', { name: f.name });
  }
  if (!f.type.startsWith('image/')) return t('« {name} » n’est pas une image.', { name: f.name });
  return null;
}

/** Ce dont la préparation a besoin du navigateur — injecté pour que la décision reste testable. */
export interface OutilsNavigateur {
  decoder(f: File): Promise<{ width: number; height: number; close?: () => void }>;
  encoder(
    bitmap: { width: number; height: number },
    largeur: number,
    hauteur: number,
    qualite: number,
  ): Promise<Blob | null>;
}

/**
 * Les outils RÉELS du navigateur.
 * ⚠️ `imageOrientation: 'from-image'` EST INDISPENSABLE, et son absence est le piège classique : les
 * photos de téléphone portent leur rotation en métadonnée EXIF. Sans cette option, le canvas les
 * dessine dans leur orientation BRUTE — et tout le catalogue se retrouve couché, silencieusement.
 */
export const outilsDuNavigateur: OutilsNavigateur = {
  decoder: (f) => createImageBitmap(f, { imageOrientation: 'from-image' }),
  encoder: async (bitmap, largeur, hauteur, qualite) => {
    const toile = document.createElement('canvas');
    toile.width = largeur;
    toile.height = hauteur;
    const ctx = toile.getContext('2d');
    if (!ctx) return null;
    // Un rendu de qualité au rééchantillonnage : sans lui, une réduction de 4032 à 1600 px crénèle.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap as unknown as CanvasImageSource, 0, 0, largeur, hauteur);
    return new Promise<Blob | null>((res) => toile.toBlob(res, 'image/webp', qualite));
  },
};

/**
 * PRÉPARE UN FICHIER — le rend prêt pour l'envoi, ou le rend TEL QUEL s'il passait déjà.
 *
 * ⚠️ UN ÉCHEC DE PRÉPARATION N'EST PAS UN ÉCHEC DE DÉPÔT : si le navigateur ne sait pas décoder ou
 * encoder, on envoie l'original et c'est le SERVEUR qui tranche. Refuser ici sur une panne de canvas
 * priverait l'opérateur d'un dépôt que le serveur aurait accepté.
 * ⚠️ ON NE GARDE LE RÉSULTAT QUE S'IL EST PLUS LÉGER : recompresser peut alourdir (un PNG plat de
 * 20 Ko devient un WebP de 40 Ko). Dans ce cas l'original gagne — on ne dégrade jamais pour rien.
 */
export async function preparerPhoto(
  f: File,
  plafondOctets: number,
  outils: OutilsNavigateur = outilsDuNavigateur,
  ciblePx: number = CIBLE_PX,
): Promise<PhotoPreparee> {
  const intact: PhotoPreparee = { fichier: f, octetsAvant: f.size, octetsApres: f.size, retouchee: false, vignette: null };
  try {
    const bitmap = await outils.decoder(f);
    const d = decisionDeRedimension(bitmap.width, bitmap.height, f.size, plafondOctets, ciblePx);
    // ⚠️ LA VIGNETTE SE FABRIQUE MÊME QUAND L'IMAGE PASSAIT DÉJÀ : sa raison d'être n'est pas le mur du
    // serveur mais le POIDS DES LISTES, et une photo de 400 Ko déjà conforme pèse tout autant sur une
    // page de vingt-cinq lignes qu'une photo qu'on a dû réduire.
    const vignette = await fabriquerVignette(f, bitmap, outils);
    if (!d.redimensionner && !d.recompresser) { bitmap.close?.(); return { ...intact, vignette }; }

    const blob = await outils.encoder(bitmap, d.largeur, d.hauteur, QUALITE_WEBP);
    bitmap.close?.();
    if (!blob || blob.size >= f.size) return { ...intact, vignette };

    const nom = f.name.replace(/\.[^.]+$/, '') || 'photo';
    return {
      fichier: new File([blob], `${nom}.webp`, { type: 'image/webp' }),
      octetsAvant: f.size,
      octetsApres: blob.size,
      retouchee: true,
      vignette,
    };
  } catch {
    // Le serveur reste juge : on lui envoie l'original plutôt que de bloquer sur une panne locale.
    return intact;
  }
}

/**
 * LA VIGNETTE — au plus `VIGNETTE_PX` de côté.
 * ⚠️ SON ÉCHEC N'EST PAS CELUI DU DÉPÔT : elle est un confort de chargement, pas la photo. Un encodeur
 * qui ne répond pas rend `null`, la lecture retombe sur l'image pleine, et le dépôt continue. C'est le
 * seul best-effort de ce module, et il est borné à ça.
 */
async function fabriquerVignette(
  f: File, bitmap: { width: number; height: number }, outils: OutilsNavigateur,
): Promise<File | null> {
  try {
    const grand = Math.max(bitmap.width, bitmap.height);
    // Déjà petite : la réencoder ne gagnerait rien et ferait perdre de la qualité pour du vent.
    if (grand <= VIGNETTE_PX) return null;
    const k = VIGNETTE_PX / grand;
    const blob = await outils.encoder(bitmap, Math.round(bitmap.width * k), Math.round(bitmap.height * k), QUALITE_VIGNETTE);
    if (!blob || blob.size >= f.size) return null;
    const nom = f.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${nom}-mini.webp`, { type: 'image/webp' });
  } catch {
    return null;
  }
}
